/**
 * RhsPanelHeader — the single canonical header for the Library right-hand-side
 * inspector (threads, records, files, email attachments) and the Otto-in-Library
 * chat previews. Modeled on the liked `ThreadDetailPanel` header.
 *
 * Contract (locked):
 *  - Container `border-b border-neutral-200 bg-neutral-50/80`, inner `px-4 py-3`.
 *  - Title `text-sm font-semibold text-neutral-900`, `min-w-0 flex-1 truncate`.
 *  - Action / X buttons share one resting style; the X is ALWAYS the final
 *    control in the right cluster (`aria-label="Close panel"`).
 *  - X is the only exit — no back button, no breadcrumb, no collapse chevron.
 *  - `onRename` => inline-rename pencil + edit field (Enter/blur commit, Escape
 *    cancel). The only internal state this component owns.
 *  - `secondRow` renders below the title row with `mt-1.5`.
 */

import {useState, type ReactNode} from 'react';
import {X, type LucideIcon} from 'lucide-react';

type RhsPanelHeaderProps = {
  title: string;
  /** Optional leading glyph. */
  icon?: LucideIcon;
  /** E.g. `<RecordStatusBadge/>`. Rendered inline after the title. */
  statusBadge?: ReactNode;
  /** When present, an inline-rename pencil shows and commits via this callback. */
  onRename?: (next: string) => void;
  /** Right-aligned, panel-specific controls. Rendered BEFORE the X. */
  actions?: ReactNode;
  /** ALWAYS renders the standard X as the final control. */
  onClose: () => void;
  /** Optional context row (assoc chips, file meta, source link). */
  secondRow?: ReactNode;
  /** Optional `title=` tooltip for truncated titles. */
  titleTitleAttr?: string;
};

export function RhsPanelHeader({
  title,
  icon: Icon,
  statusBadge,
  onRename,
  actions,
  onClose,
  secondRow,
  titleTitleAttr,
}: RhsPanelHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startRename = () => {
    setDraft(title);
    setEditing(true);
  };

  const commitRename = () => {
    setEditing(false);
    const next = draft.trim();
    if (next.length === 0 || next === title) return;
    onRename?.(next);
  };

  return (
    <div className="border-b border-neutral-200 bg-neutral-50/80">
      <div className="px-4 py-3">
        <div className="flex items-start gap-2">
          {editing ? (
            <input
              autoFocus
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
              }}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setEditing(false);
              }}
              className="min-w-0 flex-1 rounded border border-neutral-300 px-1.5 py-0.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {Icon && <Icon className="h-4 w-4 shrink-0 text-neutral-400" />}
              {onRename ? (
                <button
                  type="button"
                  onClick={startRename}
                  title={titleTitleAttr ?? title}
                  aria-label="Rename"
                  className="-mx-1 min-w-0 flex-1 truncate rounded px-1 py-0.5 text-left text-sm font-semibold text-neutral-900 transition-colors hover:bg-neutral-100"
                >
                  {title}
                </button>
              ) : (
                <h2
                  className="min-w-0 flex-1 truncate text-sm font-semibold text-neutral-900"
                  title={titleTitleAttr ?? title}
                >
                  {title}
                </h2>
              )}
              {statusBadge}
            </div>
          )}

          <div className="flex shrink-0 items-center gap-1">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close panel"
              className="rounded p-1.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {secondRow && <div className="mt-1.5">{secondRow}</div>}
      </div>
    </div>
  );
}

export default RhsPanelHeader;
